import React, { useEffect, useState, useMemo } from "react";
import { Modal, SearchableSelect } from "../../../components/ui";
import { SpaceInput } from "../../../components/ui/Input";
import { SpaceButton } from "../../../components/ui/Button";
import { useContacts } from "../../contacts/hooks/useContacts";
import toast from "react-hot-toast";

export const BookEntryModal = ({
  isOpen,
  onClose,
  onSave,
  mode = "add",
  initialData = null
}) => {
  const { contacts = [] } = useContacts();

  const [formData, setFormData] = useState({
    customerId: "",
    customerName: "",
    sdId: "",
    contact: "",
    customerType: "",
    grandTotal: "",
    materialAmount: "",
    transport: "",
    carpenterAmount: "",
    iron: "",
    hardware: "",
    puPaints: "",
    designAmount: "",
    isUPVC: false,
    upvcAmount: "",
    notes: ""
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          customerId: initialData.customerId || "",
          customerName: initialData.customerName || "",
          sdId: initialData.sdId || "",
          contact: initialData.contact || "",
          customerType: initialData.customerType || "",
          grandTotal: initialData.grandTotal !== undefined ? String(initialData.grandTotal) : "",
          materialAmount: initialData.materialAmount !== undefined ? String(initialData.materialAmount) : "",
          transport: initialData.transport !== undefined ? String(initialData.transport) : "",
          carpenterAmount: initialData.carpenterAmount !== undefined ? String(initialData.carpenterAmount) : "",
          iron: initialData.iron !== undefined ? String(initialData.iron) : "",
          hardware: initialData.hardware !== undefined ? String(initialData.hardware) : "",
          puPaints: initialData.puPaints !== undefined ? String(initialData.puPaints) : "",
          designAmount: initialData.designAmount !== undefined ? String(initialData.designAmount) : "",
          isUPVC: initialData.isUPVC || false,
          upvcAmount: initialData.upvcAmount !== undefined ? String(initialData.upvcAmount) : "",
          notes: initialData.notes || ""
        });
      } else {
        setFormData({
          customerId: "",
          customerName: "",
          sdId: "",
          contact: "",
          customerType: "",
          grandTotal: "",
          materialAmount: "",
          transport: "",
          carpenterAmount: "",
          iron: "",
          hardware: "",
          puPaints: "",
          designAmount: "",
          isUPVC: false,
          upvcAmount: "",
          notes: ""
        });
      }
      setErrors({});
    }
  }, [isOpen, initialData]);

  const customerInfoOptions = useMemo(() => {
    return contacts.map(c => {
      const name = c.customerName || `${c.firstName || ""} ${c.lastName || ""}`.trim() || "Unknown";
      const sdId = c.sdId || c.sd_id || c.customId || "";
      const displayLabel = sdId ? `${name} (${sdId})` : name;
      return {
        value: c._id,
        label: displayLabel
      };
    });
  }, [contacts]);

  const handleSelectCustomer = (customerId) => {
    if (mode === "view") return;
    if (!customerId) {
      setFormData(prev => ({
        ...prev,
        customerId: "",
        customerName: "",
        sdId: "",
        contact: "",
        customerType: ""
      }));
      return;
    }
    const cust = contacts.find(c => c._id === customerId);
    if (cust) {
      setFormData(prev => ({
        ...prev,
        customerId: cust._id,
        customerName: cust.customerName || `${cust.firstName || ""} ${cust.lastName || ""}`.trim(),
        sdId: cust.sdId || cust.sd_id || cust.customId || "",
        contact: cust.phone || cust.phoneNumber || "",
        customerType: cust.salesType || cust.customerType || "Retail"
      }));
    }
  };

  const handleInputChange = (field, value) => {
    if (mode === "view") return;
    
    if (field !== 'customerId' && field !== 'customerName' && field !== 'sdId' && field !== 'contact' && field !== 'customerType' && field !== 'notes' && field !== 'isUPVC') {
      if (value !== '') {
        const numValue = Number(value);
        if (isNaN(numValue) || numValue > 100000000) return;
      }
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customerId) {
      newErrors.customer = "Please select a customer";
    }
    if (!formData.grandTotal || isNaN(formData.grandTotal) || parseFloat(formData.grandTotal) < 0) {
      newErrors.grandTotal = "Valid Grand Total is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === "view") {
      onClose();
      return;
    }
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }
    
    const cust = contacts.find(c => c._id === formData.customerId);
    
    onSave({
      ...formData,
      customerName: cust ? (cust.customerName || `${cust.firstName || ""} ${cust.lastName || ""}`.trim()) : formData.customerName,
      sdId: cust ? (cust.sdId || cust.sd_id || cust.customId) : formData.sdId,
      contact: cust ? (cust.phone || cust.phoneNumber) : formData.contact,
      customerType: cust ? (cust.salesType || cust.customerType) : formData.customerType,
      grandTotal: parseFloat(formData.grandTotal) || 0,
      materialAmount: parseFloat(formData.materialAmount) || 0,
      transport: parseFloat(formData.transport) || 0,
      carpenterAmount: parseFloat(formData.carpenterAmount) || 0,
      iron: parseFloat(formData.iron) || 0,
      hardware: parseFloat(formData.hardware) || 0,
      puPaints: parseFloat(formData.puPaints) || 0,
      designAmount: parseFloat(formData.designAmount) || 0,
      isUPVC: formData.isUPVC || false,
      upvcAmount: parseFloat(formData.upvcAmount) || 0
    });
  };

  const grandTotalValue = Number(formData.grandTotal) || 0;
  const sumOfAllEntered = 
    (Number(formData.materialAmount) || 0) + 
    (Number(formData.transport) || 0) + 
    (Number(formData.carpenterAmount) || 0) + 
    (Number(formData.iron) || 0) + 
    (Number(formData.hardware) || 0) + 
    (Number(formData.puPaints) || 0) + 
    (Number(formData.designAmount) || 0) + 
    (formData.isUPVC ? (Number(formData.upvcAmount) || 0) : 0);

  const isNotEqualTotal = sumOfAllEntered !== grandTotalValue;

  const DataField = ({ label, value }) => (
    <div className="flex flex-col gap-1">
      <span className="text-[12px] font-medium text-slate-500">{label}</span>
      <span className="text-[14px] font-medium text-slate-800">{value || '—'}</span>
    </div>
  );

  const carpenterAndIronTotal = 
    (Number(formData.carpenterAmount) || 0) + 
    (Number(formData.iron) || 0);

  const othersTotal = 
    (Number(formData.transport) || 0) + 
    (Number(formData.hardware) || 0) + 
    (Number(formData.puPaints) || 0) + 
    (Number(formData.designAmount) || 0);

  const upvcTotal = Number(formData.upvcAmount) || 0;
  const materialTotal = Number(formData.materialAmount) || 0;

  if (mode === "view") {
    const custInfo = contacts.find(c => c._id === formData.customerId);
    const displayCustName = custInfo ? (custInfo.customerName || `${custInfo.firstName || ""} ${custInfo.lastName || ""}`.trim() || "Unknown") : "—";
    const displayCustSd = custInfo ? (custInfo.sdId || custInfo.sd_id || custInfo.customId) : "";
    const displayCustFull = displayCustSd ? `${displayCustName} (${displayCustSd})` : displayCustName;

    return (
      <Modal isOpen={isOpen} onClose={onClose} size="max-w-3xl">
        <div className="flex flex-col p-2">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-medium text-gray-900">Book Entry Details</h2>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-${formData.isUPVC ? '5' : '4'} gap-4 mb-8`}>
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 flex flex-col justify-center">
              <span className="text-[12px] font-medium text-slate-500 mb-1">Total Amount</span>
              <span className={`text-xl font-bold ${isNotEqualTotal ? 'text-red-500' : 'text-slate-800'}`}>₹{grandTotalValue.toLocaleString('en-IN')}</span>
            </div>
            <div className="p-4 rounded-xl border border-emerald-100 bg-emerald-50/50 flex flex-col justify-center">
              <span className="text-[12px] font-medium text-emerald-600 mb-1">Material Amount</span>
              <span className="text-xl font-bold text-emerald-700">₹{materialTotal.toLocaleString('en-IN')}</span>
            </div>
            <div className="p-4 rounded-xl border border-blue-100 bg-blue-50 flex flex-col justify-center">
              <span className="text-[12px] font-medium text-blue-600 mb-1">Carpenter + Iron</span>
              <span className="text-xl font-bold text-blue-700">₹{carpenterAndIronTotal.toLocaleString('en-IN')}</span>
            </div>
            <div className="p-4 rounded-xl border border-brand-orange/20 bg-brand-orange/5 flex flex-col justify-center">
              <span className="text-[11px] font-medium text-brand-orange mb-1">Others (Tr, Hw, PU, De)</span>
              <span className="text-xl font-bold text-brand-dark">₹{othersTotal.toLocaleString('en-IN')}</span>
            </div>
            {formData.isUPVC && (
              <div className="p-4 rounded-xl border border-purple-100 bg-purple-50 flex flex-col justify-center">
                <span className="text-[12px] font-medium text-purple-600 mb-1">UPVC Amount</span>
                <span className="text-xl font-bold text-purple-700">₹{upvcTotal.toLocaleString('en-IN')}</span>
              </div>
            )}
          </div>
          
          {isNotEqualTotal && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-600 text-[13px] rounded-lg">
              <strong>Warning:</strong> The sum of entered amounts (₹{sumOfAllEntered.toLocaleString('en-IN')}) is {sumOfAllEntered > grandTotalValue ? `₹${(sumOfAllEntered - grandTotalValue).toLocaleString('en-IN')} more` : `₹${(grandTotalValue - sumOfAllEntered).toLocaleString('en-IN')} less`} than the Grand Total (₹{grandTotalValue.toLocaleString('en-IN')}).
            </div>
          )}

          <div className="space-y-8">
            {/* Top Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 rounded-xl p-5 border border-slate-100">
              <DataField label="Customer Info" value={displayCustFull} />
              <div>
                <span className="block text-[12px] font-medium text-slate-500 mb-1">Total Amount</span>
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-medium text-slate-800">₹{grandTotalValue.toLocaleString('en-IN') || '—'}</span>
                  {formData.isUPVC && <span className="text-[10px] font-medium text-brand-blue bg-blue-100 px-2 py-0.5 rounded-full uppercase tracking-wide">UPVC</span>}
                </div>
              </div>
            </div>

            {/* Booking Details Section */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <h3 className="text-[15px] font-semibold text-slate-800">Booking Details Breakdown</h3>
                <div className="h-px bg-slate-200 flex-1"></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
                <DataField label="Material Amount" value={`₹${Number(formData.materialAmount || 0).toLocaleString('en-IN')}`} />
                <DataField label="Transport" value={`₹${Number(formData.transport || 0).toLocaleString('en-IN')}`} />
                <DataField label="Carpenter Amount" value={`₹${Number(formData.carpenterAmount || 0).toLocaleString('en-IN')}`} />
                <DataField label="Iron" value={`₹${Number(formData.iron || 0).toLocaleString('en-IN')}`} />
                <DataField label="Hardware" value={`₹${Number(formData.hardware || 0).toLocaleString('en-IN')}`} />
                <DataField label="PU Paints" value={`₹${Number(formData.puPaints || 0).toLocaleString('en-IN')}`} />
                <DataField label="Design Amount" value={`₹${Number(formData.designAmount || 0).toLocaleString('en-IN')}`} />
                {formData.isUPVC && (
                  <DataField label="UPVC Amount" value={`₹${Number(formData.upvcAmount || 0).toLocaleString('en-IN')}`} />
                )}
              </div>
            </div>

            {/* Notes */}
            {formData.notes && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-[15px] font-semibold text-slate-800">Notes</h3>
                  <div className="h-px bg-slate-200 flex-1"></div>
                </div>
                <div className="w-full px-4 py-3 bg-yellow-50/50 border border-yellow-100 rounded-xl text-[14px] text-slate-700 min-h-[80px] whitespace-pre-wrap leading-relaxed shadow-sm">
                  {formData.notes}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-8">
            <SpaceButton type="button" variant="secondary" onClick={onClose}>
              Close
            </SpaceButton>
          </div>
        </div>
      </Modal>
    );
  }

  const labelClass = "block text-[12px] text-slate-700 mb-1";

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="max-w-xl">
      <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-2">
        <div className="flex justify-between items-center mb-1">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === "edit" ? "Edit Book Entry" : "Book Entry"}
          </h2>
        </div>
        
        {/* Customer Information */}
        <div className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Customer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelect
              label="Customer Info *"
              placeholder="Search Name or SD ID..."
              value={formData.customerId}
              options={customerInfoOptions}
              onChange={handleSelectCustomer}
              error={errors.customer}
            />

            <div className="flex flex-col gap-1 w-full relative">
              <div className="flex items-center justify-between mb-1">
                <label className="text-[12px] font-semibold text-slate-700">Grand Total <span className="text-red-500">*</span></label>
                <label className="flex items-center gap-1.5 cursor-pointer text-[12px] text-slate-600 hover:text-brand-blue transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.isUPVC}
                    onChange={(e) => handleInputChange("isUPVC", e.target.checked)}
                    className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  />
                  <span>UPVC Options</span>
                </label>
              </div>
              <SpaceInput 
                placeholder="Enter grand total amount" 
                type="number"
                value={formData.grandTotal}
                onChange={(e) => formData.isUPVC ? handleInputChange("grandTotal", e.target.value) : undefined}
                error={errors.grandTotal}
                disabled={!formData.isUPVC}
                required
              />
            </div>
          </div>
        </div>

        {/* Booking Details */}
        <div className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Booking Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SpaceInput 
              label="Material Amount" 
              placeholder="Enter material amount" 
              type="number"
              value={formData.materialAmount}
              onChange={(e) => handleInputChange("materialAmount", e.target.value)}
            />
            
            <SpaceInput 
              label="Transport" 
              placeholder="Enter transport charges" 
              type="number"
              value={formData.transport}
              onChange={(e) => handleInputChange("transport", e.target.value)}
            />

            <SpaceInput 
              label="Carpenter Amount" 
              placeholder="Enter carpenter amount" 
              type="number"
              value={formData.carpenterAmount}
              onChange={(e) => handleInputChange("carpenterAmount", e.target.value)}
            />

            <SpaceInput 
              label="Iron" 
              placeholder="Enter iron amount" 
              type="number"
              value={formData.iron}
              onChange={(e) => handleInputChange("iron", e.target.value)}
            />

            <SpaceInput 
              label="Hardware" 
              placeholder="Enter hardware charges" 
              type="number"
              value={formData.hardware}
              onChange={(e) => handleInputChange("hardware", e.target.value)}
            />

            <SpaceInput 
              label="PU Paints" 
              placeholder="Enter PU paints charges" 
              type="number"
              value={formData.puPaints}
              onChange={(e) => handleInputChange("puPaints", e.target.value)}
            />

            <SpaceInput 
              label="Design Amount" 
              placeholder="Enter design charges" 
              type="number"
              value={formData.designAmount}
              onChange={(e) => handleInputChange("designAmount", e.target.value)}
            />
            
            {formData.isUPVC && (
              <SpaceInput 
                label="UPVC Amount" 
                placeholder="Enter UPVC charges" 
                type="number"
                value={formData.upvcAmount}
                onChange={(e) => handleInputChange("upvcAmount", e.target.value)}
              />
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Notes</label>
          <textarea
            rows={4}
            placeholder="Enter booking notes..."
            value={formData.notes}
            onChange={(e) => handleInputChange("notes", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
          />
        </div>

        {isNotEqualTotal && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-[13px] rounded-lg">
            <strong>Warning:</strong> The sum of entered amounts (₹{sumOfAllEntered.toLocaleString('en-IN')}) is {sumOfAllEntered > grandTotalValue ? `₹${(sumOfAllEntered - grandTotalValue).toLocaleString('en-IN')} more` : `₹${(grandTotalValue - sumOfAllEntered).toLocaleString('en-IN')} less`} than the Grand Total (₹{grandTotalValue.toLocaleString('en-IN')}).
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-2">
          <SpaceButton 
            type="button" 
            variant="secondary" 
            onClick={onClose}
          >
            Cancel
          </SpaceButton>
          <SpaceButton 
            type="submit" 
            variant="primary"
            className="btn-primary" 
            disabled={isNotEqualTotal}
          >
            {mode === "edit" ? "Save Changes" : "Save"}
          </SpaceButton>
        </div>
      </form>
    </Modal>
  );
};
